import { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, Calendar, MapPin, Users, Copy, CheckCircle, Loader2, Share2, MessageCircle, Edit2, Download } from 'lucide-react';
import { toast } from 'sonner';
import AuthModal from '../components/AuthModal';
import RSVPModal from '../components/RSVPModal';
import ShareEventModal from '../components/ShareEventModal';
import EditRSVPContributionModal from '../components/EditRSVPContributionModal';

const EventDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [rsvpStats, setRsvpStats] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [myRsvp, setMyRsvp] = useState(null);
  const [showEditRsvpModal, setShowEditRsvpModal] = useState(false);

  useEffect(() => {
    fetchEvent();
    fetchRSVPStats();
    if (user) {
      fetchMyRsvp();
    }
  }, [id, user]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${id}`);
      setEvent(response.data);
    } catch (error) {
      toast.error('Event not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchRSVPStats = async () => {
    try {
      const response = await axios.get(`${API}/events/${id}/rsvp-stats`);
      setRsvpStats(response.data);
    } catch (error) {
      console.error('Failed to fetch RSVP stats');
    }
  };

  const fetchMyRsvp = async () => {
    try {
      const response = await axios.get(`${API}/rsvps/my-rsvps`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const eventRsvp = response.data.find(r => r.event_id === id);
      setMyRsvp(eventRsvp || null);
    } catch (error) {
      console.error('Failed to fetch my RSVP');
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(event.event_code);
    setCopied(true);
    toast.success('Event code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleContribute = (goal) => {
    if (!user) {
      setShowAuthModal(true);
      setSelectedGoal(goal);
      return;
    }
    setSelectedGoal(goal);
    setShowRSVPModal(true);
  };

  const handleRSVP = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    // Don't set a specific goal - modal will show all goals
    setSelectedGoal(null);
    setShowRSVPModal(true);
  };

  const handleRSVPSuccess = () => {
    setShowRSVPModal(false);
    setShowEditRsvpModal(false);
    fetchEvent();
    fetchRSVPStats();
    if (user) {
      fetchMyRsvp();
    }
  };

  const handleDownloadPhoto = (photoUrl, index) => {
    try {
      const link = document.createElement('a');
      link.href = photoUrl;
      link.download = `${event.title.replace(/[^a-z0-9]/gi, '_')}_photo_${index + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Photo download started');
    } catch (error) {
      toast.error('Failed to download photo');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-contribo-navy" />
      </div>
    );
  }

  if (!event) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-contribo-navy from-opacity-5 via-contribo-gold via-opacity-5 to-blue-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6"
          data-testid="back-home-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        {/* Event Details */}
        <Card className="glass-card mb-6" data-testid="event-details-card">
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex-1">
                <CardTitle className="text-3xl mb-2">{event.title}</CardTitle>
                <p className="text-gray-600 mb-2">Hosted by {event.host_name}</p>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    <span>{new Date(event.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                {/* Show Event Code only if approved OR not the host */}
                {(user && event.host_id === user.id && event.approval_status === 'pending') ? (
                  <div className="flex items-center space-x-2 bg-yellow-100 px-4 py-2 rounded-full border-2 border-yellow-300">
                    <span className="text-sm font-medium text-yellow-900">Event ID:</span>
                    <span className="text-lg font-bold text-yellow-700">
                      Pending Verification
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2 bg-contribo-navy bg-opacity-100 px-4 py-2 rounded-full">
                    <span className="text-sm font-medium text-white">Event ID:</span>
                    <span className="text-2xl font-bold text-contribo-gold tracking-wider">
                      {event.event_code}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCopyCode}
                      data-testid="copy-code-btn"
                    >
                      {copied ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <Copy className="h-5 w-5 text-white" />
                      )}
                    </Button>
                  </div>
                )}
                {user && event.host_id === user.id && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowShareModal(true)}
                    disabled={event.approval_status === 'pending'}
                    className="border-contribo-navy text-contribo-navy hover:bg-contribo-navy bg-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="share-event-btn"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    {event.approval_status === 'pending' ? 'Pending Approval' : 'Share Event'}
                  </Button>
                )}
                
                {/* View Photos Button (for all RSVP'd users, hosts, co-hosts) */}
                {user && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/event/${id}/photos`)}
                    className="border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Event Photos
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Approval Status for Event Creator */}
            {user && event.host_id === user.id && (
              <>
                {event.approval_status === 'pending' && (
                  <div className="mb-4 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                    <p className="font-semibold text-yellow-900">⏳ Payment Verification Pending</p>
                    <p className="text-sm text-yellow-800 mt-1">
                      Your payment is being verified by Contribo admin. Event sharing will be enabled after approval.
                    </p>
                    <p className="text-xs text-yellow-700 mt-2">
                      Verification Time: 8 AM-8 PM (3-6 hrs) | 8 PM-8 AM (6-10 hrs)
                    </p>
                  </div>
                )}
                {event.approval_status === 'approved' && (
                  <div className="mb-4 p-4 bg-green-50 border-2 border-green-300 rounded-lg">
                    <p className="font-bold text-green-900 text-lg">✅ Payment Confirmed!</p>
                    <p className="text-sm text-green-800 mt-1">
                      Your event payment has been verified by Contribo admin. Go ahead and share your event details with your guests!
                    </p>
                  </div>
                )}
                {event.approval_status === 'rejected' && (
                  <div className="mb-4 p-4 bg-red-50 border-2 border-red-300 rounded-lg">
                    <p className="font-semibold text-red-900">❌ Payment Verification Failed</p>
                    <p className="text-sm text-red-800 mt-1">
                      Your payment could not be verified. Please contact Contribo support.
                    </p>
                  </div>
                )}
              </>
            )}
            {event.description && (
              <p className="text-gray-700 mb-4">{event.description}</p>
            )}
            {event.blessings && (
              <div className="mb-4 p-4 bg-contribo-gold bg-opacity-10 rounded-lg border-l-4 border-contribo-gold">
                <p className="text-sm font-semibold text-contribo-navy mb-1">✨ Blessings & Message</p>
                <p className="text-gray-700 italic">{event.blessings}</p>
              </div>
            )}
            <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
              <div className="flex items-center">
                <Calendar className="h-5 w-5 mr-2 text-contribo-navy" />
                {new Date(event.date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
              <div className="flex items-center">
                <MapPin className="h-5 w-5 mr-2 text-contribo-navy" />
                {event.location}
              </div>
              <div className="flex items-center">
                <Users className="h-5 w-5 mr-2 text-contribo-navy" />
                {event.total_rsvps} Total RSVPs
              </div>
              {event.expected_guests && (
                <div className="flex items-center">
                  <Users className="h-5 w-5 mr-2 text-contribo-gold" />
                  <span className="font-semibold text-contribo-navy">
                    Expected: {event.expected_guests} guests
                  </span>
                </div>
              )}
            </div>
            
            {rsvpStats && (
              <div className="flex flex-wrap gap-3 mb-4">
                <div className="bg-green-100 px-4 py-2 rounded-full">
                  <span className="text-sm font-semibold text-green-700">
                    ✓ Coming: {rsvpStats.yes}
                  </span>
                </div>
                <div className="bg-red-100 px-4 py-2 rounded-full">
                  <span className="text-sm font-semibold text-red-700">
                    ✗ Not Coming: {rsvpStats.no}
                  </span>
                </div>
                <div className="bg-yellow-100 px-4 py-2 rounded-full">
                  <span className="text-sm font-semibold text-yellow-700">
                    ? Maybe: {rsvpStats.maybe}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* My RSVP Status */}
        {user && myRsvp && (
          <Card className="glass-card mb-6 border-2 border-purple-200" data-testid="my-rsvp-card">
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Your RSVP</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Status:</span>
                      <span
                        className={`text-sm px-3 py-1 rounded-full font-semibold ${
                          myRsvp.rsvp_status === 'yes'
                            ? 'bg-green-100 text-green-700'
                            : myRsvp.rsvp_status === 'no'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {myRsvp.rsvp_status === 'yes' ? '✓ Coming' : myRsvp.rsvp_status === 'no' ? '✗ Not Coming' : '? Maybe'}
                      </span>
                    </div>
                    
                    {myRsvp.total_guests > 0 && (
                      <div className="text-sm">
                        <span className="text-gray-600">Guests: </span>
                        <span className="font-semibold text-contribo-navy">{myRsvp.total_guests}</span>
                        <span className="text-xs text-gray-500 ml-2">
                          (Adults: {myRsvp.adults_male + myRsvp.adults_female}, Kids: {myRsvp.kids_male + myRsvp.kids_female})
                        </span>
                      </div>
                    )}
                    
                    {myRsvp.contribution_amount && myRsvp.contribution_amount > 0 && (
                      <div className="text-sm">
                        <span className="text-gray-600">Contribution: </span>
                        <span className="font-bold text-green-600">₹{myRsvp.contribution_amount.toLocaleString()}</span>
                        {myRsvp.contribution_goal_title && (
                          <span className="text-xs text-gray-500 ml-2">to {myRsvp.contribution_goal_title}</span>
                        )}
                      </div>
                    )}
                    
                    {myRsvp.message && (
                      <div className="text-sm bg-contribo-navy bg-opacity-50 p-2 rounded">
                        <span className="text-gray-600">Message: </span>
                        <span className="italic">"{myRsvp.message}"</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  {myRsvp.edit_count < 3 && (
                    <Button
                      className="bg-gradient-to-r from-contribo-navy to-contribo-gold text-white"
                      onClick={() => setShowEditRsvpModal(true)}
                      data-testid="edit-my-rsvp-btn"
                    >
                      <Edit2 className="mr-2 h-4 w-4" />
                      Edit RSVP & Contribution ({3 - myRsvp.edit_count} left)
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Single RSVP & Contribute Button */}
        {!myRsvp && user && (
          <div className="mb-6 text-center">
            <Button
              size="lg"
              className="bg-gradient-to-r from-contribo-navy to-contribo-gold text-white px-12 py-6 text-lg"
              onClick={handleRSVP}
              data-testid="rsvp-contribute-main-btn"
            >
              <Users className="mr-2 h-6 w-6" />
              RSVP & Contribute
            </Button>
          </div>
        )}

        {/* Event Photos */}
        {event.event_photos && event.event_photos.length > 0 && (
          <Card className="glass-card mb-6">
            <CardHeader>
              <CardTitle className="text-2xl">📸 Event Photos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {event.event_photos.map((photo, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={photo}
                      alt={`${event.title} photo ${index + 1}`}
                      className="w-full h-40 object-cover rounded-lg border-2 border-contribo-navy shadow-md"
                    />
                    <button
                      onClick={() => handleDownloadPhoto(photo, index)}
                      className="absolute bottom-2 right-2 bg-contribo-navy text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-contribo-gold"
                      title="Download photo"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contribution Goals - View Only */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Contribution Goals</h2>
          <div className="grid gap-4" data-testid="contribution-goals-list">
            {event.contribution_goals.map((goal) => {
              const progress = (goal.current_amount / goal.target_amount) * 100;
              return (
                <Card
                  key={goal.id}
                  className="glass-card event-card"
                  data-testid={`goal-card-${goal.id}`}
                >
                  <CardContent className="pt-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-xl font-semibold text-gray-900">
                            {goal.title}
                          </h3>
                          {goal.wishlist_item_id && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-semibold">
                              From Wishlist
                            </span>
                          )}
                          {goal.is_completed && (
                            <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-bold flex items-center gap-1">
                              <CheckCircle className="h-3 w-3" />
                              100% Completed
                            </span>
                          )}
                        </div>
                        {goal.description && (
                          <p className="text-gray-600 text-sm mb-4">{goal.description}</p>
                        )}
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">
                              Raised: ₹{goal.current_amount.toLocaleString()}
                            </span>
                            <span className="text-gray-600">
                              Goal: ₹{goal.target_amount.toLocaleString()}
                            </span>
                          </div>
                          <Progress value={progress} className={`h-3 ${goal.is_completed ? 'bg-green-200' : ''}`} />
                          <p className={`text-sm font-semibold ${goal.is_completed ? 'text-green-600' : 'text-contribo-navy'}`}>
                            {progress.toFixed(1)}% Complete
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAuthModal && (
        <AuthModal
          onClose={() => setShowAuthModal(false)}
          onSuccess={() => {
            setShowAuthModal(false);
            if (selectedGoal) {
              setShowRSVPModal(true);
            }
          }}
        />
      )}
      {showRSVPModal && (
        <RSVPModal
          event={event}
          goal={null}
          onClose={() => setShowRSVPModal(false)}
          onSuccess={handleRSVPSuccess}
        />
      )}
      {showShareModal && (
        <ShareEventModal
          event={event}
          onClose={() => setShowShareModal(false)}
        />
      )}
      {showEditRsvpModal && myRsvp && (
        <EditRSVPContributionModal
          event={event}
          rsvp={myRsvp}
          onClose={() => setShowEditRsvpModal(false)}
          onSuccess={handleRSVPSuccess}
        />
      )}
      <Footer />
    </div>
  );
};

export default EventDetailsPage;
